import { ClippingStudioSubtitleStyle } from "./clipping-studio-transcript";

/** Video track segment: in/out timestamps and display order. */
export interface ClippingStudioVideoSegment {
  id: string;
  label: string;
  order: number;
  inMs: number;
  outMs: number;
  /** Data URL of the first frame of the segment (thumbnail). */
  thumbnailFirstFrameUrl?: string | null;
  /** Data URL of the last frame of the segment (thumbnail). */
  thumbnailLastFrameUrl?: string | null;
  /** When set, segment is MAM-sourced (Edit mode clip); persisted for round-trip. */
  mamMediaId?: number;
  /**
   * Selected linked audio track for this MAM clip (persisted for render).
   * Omit / unset = embedded video audio.
   */
  audioMediaId?: number;
  /** Preview URL for MAM-sourced segment (may expire; refresh from mamMediaId on load). */
  previewUrl?: string;
  /** Source timeline appended segment that produced this MAM clip. */
  appendedSegmentId?: string;
}

/** Marker: point on timeline (in only) with free text. */
export interface ClippingStudioMarkerEvent {
  id: string;
  inMs: number;
  text: string;
  /** When set, marker is for an appended MAM segment (persisted for round-trip). */
  appendedSegmentId?: string;
}

/** Subtitle cue — field names match media / Content Control JSON. */
export interface ClippingStudioSubtitleEvent {
  id: string;
  StartTimeMS: number;
  EndTimeMS: number;
  Text: string;
  Commands?: string;
  language?: string;
  /** When true, cue comes from main-source media track (hide on MAM clips in Edit). When false/undefined, user-added. */
  fromMainSource?: boolean;
}

/** Text segment: in/out, free text, and text styling. */
export type TextAlignment = "left" | "center" | "right";

export interface ClippingStudioTextEvent {
  id: string;
  inMs: number;
  outMs: number;
  text: string;
  fontSize?: number;
  alignment?: TextAlignment;
  bold?: boolean;
  italic?: boolean;
  fontColor?: string;
  backgroundColor?: string;
  /** Padding (px) around text when drawing background. Ignored if backgroundColor is transparent. */
  backgroundPadding?: number;
  /** Border radius (px) of the background rectangle. 0 = square corners. */
  backgroundBorderRadius?: number;
  /** When true, draw text with a drop shadow. */
  dropShadow?: boolean;
  shadowColor?: string;
  shadowBlur?: number;
  shadowOffsetX?: number;
  shadowOffsetY?: number;
  fontFamily?: string;
  fontWeight?: string;
  fontStyle?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
}

/** Graphics segment: in/out with visual/editing properties. */
export type GraphicType = "logo" | "fruit-icon" | "emoji" | "gfx";

export type LogoKey = "RFI" | "france24";

export interface ClippingStudioGraphicEvent {
  id: string;
  inMs: number;
  outMs: number;
  type: GraphicType;
  mainText: string;
  subText: string;
  color: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** For type === "logo": which logo image to use. */
  logoKey?: LogoKey;
  /** For type === "fruit-icon": which fruit icon to use (e.g. "Icon 1" … "Icon 25"). */
  iconKey?: string;
  /** For type === "emoji": the emoji character to display (e.g. "😀"). */
  emojiChar?: string;
  /** For type === "gfx": backend graphics instance id. */
  gfxInstanceId?: number;
  /** For type === "gfx": template query parameters (TEXT1, TEXT2, etc.). */
  gfxParams?: Record<string, string>;
  /** For type === "gfx": true when the GFX package is classified as SQZ (squeeze). Absent or false means non-sqz. */
  isSqz?: boolean;
  /** Scale factor for logo/icon/emoji/gfx (0.1–3.0). Default 1. */
  scale?: number;
  /** Rotation in degrees (0–360). Default 0. */
  rotationDeg?: number;
}

/** Audio segment: in/out, pointer to media, and display properties. */
export interface ClippingStudioAudioEvent {
  id: string;
  inMs: number;
  outMs: number;
  mediaId: number;
  /** When true, this event stores the main-source linked-track selection (not a catalog overlay). */
  isMainSource?: boolean;
  /** Catalog audio UI fields (optional; unused for main-source selection events). */
  title?: string;
  artist?: string;
  durationLabel?: string;
  artworkUrl?: string;
  artworkColor?: string;
  isBookmarked?: boolean;
}

/** Layout mode for reframing a segment to a different aspect ratio. */
export type ReframeLayoutMode = "fill" | "fit" | "customBackground" | "split50";

/** Background style for letterbox/background areas (fit, custom, split). */
export type ReframeBackgroundStyle = "black" | "white" | "blur";

/** Normalized crop rectangle over 16:9 source (0–1). */
export interface ReframeCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Reframe config for one segment at one destination. */
export interface ReframeConfig {
  layout: ReframeLayoutMode;
  /** For fit / custom / split when there is letterbox or background area. */
  backgroundStyle?: ReframeBackgroundStyle;
  /** Single crop for fill or customBackground. */
  crop?: ReframeCropRect;
  /** For split50: top and bottom crop rects (each 50% height, horizontal movement only). */
  topCrop?: ReframeCropRect;
  bottomCrop?: ReframeCropRect;
  /** For split50: when true, bottomCrop is drawn on top half of output. */
  swapped?: boolean;
}

/** Keyframe for reframing: timestamp within clip (ms) and reframe config. */
export interface ReframeKeyframe {
  timeMs: number;
  config: ReframeConfig;
}

/**
 * Returns the reframe config that applies at the given time within a clip.
 * Uses nearest previous keyframe; if time is before all keyframes, uses the first keyframe.
 */
export function getReframeConfigAtTime(
  keyframes: ReframeKeyframe[],
  timeMsWithinClip: number,
): ReframeConfig | null {
  if (!keyframes?.length) return null;
  const sorted = [...keyframes].sort((a, b) => a.timeMs - b.timeMs);
  let chosen = sorted[0];
  for (const kf of sorted) {
    if (kf.timeMs <= timeMsWithinClip) chosen = kf;
    else break;
  }
  return chosen.config;
}

/** Type guard: value is legacy single ReframeConfig (object with layout, not a keyframe array). */
function isLegacyReframeConfig(
  val: ReframeConfig | ReframeKeyframe[] | undefined,
): val is ReframeConfig {
  return (
    val != null &&
    typeof val === "object" &&
    !Array.isArray(val) &&
    "layout" in val
  );
}

/**
 * Normalizes segment reframes from persisted shape: legacy ReframeConfig becomes one keyframe at 0.
 */
export function normalizeSegmentReframes(
  segmentReframes: Record<string, ReframeConfig | ReframeKeyframe[]> | undefined,
): Record<string, ReframeKeyframe[]> {
  if (!segmentReframes) return {};
  return Object.fromEntries(
    Object.entries(segmentReframes).map(([id, val]) => {
      if (isLegacyReframeConfig(val)) {
        return [id, [{ timeMs: 0, config: val }]];
      }
      return [id, val as ReframeKeyframe[]];
    }),
  );
}

/**
 * Per-destination variant: delta overrides for base elements plus destination-only elements.
 * Key = base element id for overrides; destination-only elements are full events.
 */
export interface DestinationVariant {
  /** Partial overrides for base text (id -> partial event). */
  textOverrides?: Record<string, Partial<ClippingStudioTextEvent>>;
  /** Partial overrides for base graphics (id -> partial event). */
  graphicOverrides?: Record<string, Partial<ClippingStudioGraphicEvent>>;
  /** Text elements that exist only on this destination (full events). */
  textOnly?: ClippingStudioTextEvent[];
  /** Graphic elements that exist only on this destination (full events). */
  graphicOnly?: ClippingStudioGraphicEvent[];
  /** IDs of base text to hide for this destination only (e.g. user removed from this dest). */
  textExcludedIds?: string[];
  /** IDs of base graphics to hide for this destination only (e.g. user removed from this dest). */
  graphicExcludedIds?: string[];
  /** Per-segment reframe keyframes for this destination. Key = segment (clip) id. */
  segmentReframes?: Record<string, ReframeKeyframe[]>;
}

/** Appended MAM video segment on the source timeline (preview URL, not transcoded). */
export interface AppendedMamSegment {
  id: string;
  mamMediaId: number;
  durationMs: number;
  previewUrl?: string;
  /** Display name for the segment (e.g. media title); shown on the MAM source marker. */
  label?: string;
  /**
   * Selected linked audio track id for this MAM segment (persisted for render).
   * Omit / unset = embedded video audio.
   */
  audioMediaId?: number;
  /**
   * Runtime mirror of audioMediaId used by preview players.
   * Refreshed on open; not the save contract (save writes audioMediaId).
   */
  defaultAudioMediaId?: number;
  /** Presigned preview URL for the selected MAM audio track. Runtime-only. */
  defaultAudioPreviewUrl?: string;
}

/** Root project: one object per POC, stored/read as a whole. */
export interface ClippingStudioProject {
  /** Backend project id; set when loaded from API. Used for project-scoped APIs (e.g. find-clip recent prompts). */
  id?: number;
  mediaId: number;
  /** Display name for the project; optional for backwards compatibility. */
  title?: string;
  /** Raw persisted clip preview payload: legacy data URL or v1 JSON model. */
  clipPreview?: string;
  /** Main source media JSON (name, preview_url, thumbnails, etc.). Round-tripped from API; required by backend on save. */
  mainSource?: string;
  /** Frames per second; round-tripped from API. Send real value on save (not 0). */
  fps?: number;
  video: ClippingStudioVideoSegment[];
  markers: ClippingStudioMarkerEvent[];
  subtitles: ClippingStudioSubtitleEvent[];
  /** Per-track subtitle edits only (overlay on originals). Key = language or imported `srt-N` / `vtt-N`. */
  subtitleEdits?: Record<string, ClippingStudioSubtitleEvent[]>;
  /** Subtitle rendering style (font, colors, position). Persisted in project row. */
  subtitleStyle?: ClippingStudioSubtitleStyle;
  text: ClippingStudioTextEvent[];
  graphics: ClippingStudioGraphicEvent[];
  audio: ClippingStudioAudioEvent[];
  /** Per-destination variant data. Key = destination id (SocialAccount.id). */
  destinationVariants?: Record<string, DestinationVariant>;
  /** MAM video segments appended to the source timeline (Source mode). */
  appendedMamSegments?: AppendedMamSegment[];
  /** Find-clip recent prompts JSON array. Round-tripped from API; required by backend on save. */
  findClipRecentPrompts?: string;
  /** Raw publish metadata JSON from open/save API (publish tab). */
  publishMetadataJson?: string;
  /** Raw per-destination metadata overrides JSON from open/save API. */
  publishMetadataOverridesJson?: string;
}


